import { useState, useEffect, useRef } from 'react';
import { Loader2, X } from 'lucide-react';
import type { WebhookLogDetail } from '../../../api/webhook-logs';
import { fetchWebhookLogDetail } from '../../../api/webhook-logs';

function statusBadge(status: string) {
  const colors: Record<string, string> = {
    received: 'bg-water-100 text-water-800 dark:bg-water-900/30 dark:text-water-300',
    processed: 'bg-moss-100 text-moss-800 dark:bg-moss-900/30 dark:text-moss-300',
    error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-stone-100 text-stone-700 dark:bg-stone-800 dark:text-stone-300'}`}>
      {status}
    </span>
  );
}

function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function WebhookLogDetailModal({
  logId,
  onClose,
}: {
  logId: string;
  onClose: () => void;
}) {
  const [log, setLog] = useState<WebhookLogDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    fetchWebhookLogDetail(logId)
      .then(setLog)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [logId]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleBackdrop = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdrop}
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-12 overflow-y-auto"
    >
      <div className="bg-white dark:bg-[#1a1f1c] rounded-xl shadow-2xl w-full max-w-3xl mb-12 max-h-[calc(100vh-6rem)] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-[#1a1f1c] border-b px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
          <div>
            {loading ? (
              <div className="flex items-center gap-2 text-stone-500 dark:text-stone-400">
                <Loader2 size={16} className="animate-spin" /> Loading...
              </div>
            ) : error ? (
              <span className="text-red-600">{error}</span>
            ) : (
              <>
                <h2 className="text-lg font-bold text-bark-900 dark:text-[#e8ece9]">
                  Webhook Log #{log!.id}
                </h2>
                <p className="text-sm text-stone-500 dark:text-stone-400 mt-0.5">
                  {formatDateTime(log!.received_at)}
                </p>
              </>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-stone-100 dark:hover:bg-[#242d28] rounded-lg">
            <X size={18} />
          </button>
        </div>

        {log && !loading && !error && (
          <div className="px-6 py-5 space-y-6">
            {/* Metadata grid */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Source', value: log.source },
                { label: 'Event Type', value: log.event_type ?? '--' },
                { label: 'Status', value: null, badge: log.status },
                { label: 'IP Address', value: log.ip_address ?? '--' },
                { label: 'Run ID', value: log.run_id ?? '--', mono: true },
                { label: 'Processing', value: log.processing_ms !== null && log.processing_ms !== undefined ? `${log.processing_ms}ms` : '--' },
                { label: 'Bronze Table', value: log.bronze_table ?? '--' },
                { label: 'Record PK', value: log.record_pk ?? '--', mono: true },
                { label: 'Dedup Result', value: log.dedup_result ?? '--' },
              ].map((item) => (
                <div key={item.label} className="bg-stone-50 dark:bg-[#1e2520] rounded-lg p-3">
                  <p className="text-xs text-stone-500 dark:text-stone-400">{item.label}</p>
                  {item.badge ? (
                    <div className="mt-1">{statusBadge(item.badge)}</div>
                  ) : (
                    <p className={`text-sm font-medium text-bark-900 dark:text-[#e8ece9] mt-0.5 ${item.mono ? 'font-mono text-xs' : ''}`}>
                      {item.value}
                    </p>
                  )}
                </div>
              ))}
            </div>

            {/* Error message */}
            {log.error_message && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">Error</p>
                <p className="text-sm text-red-700 dark:text-red-400 font-mono whitespace-pre-wrap">{log.error_message}</p>
              </div>
            )}

            {/* Request headers */}
            {log.request_headers && Object.keys(log.request_headers).length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-bark-900 dark:text-[#e8ece9] mb-2">Request Headers</h3>
                <div className="bg-stone-50 dark:bg-[#1e2520] rounded-lg p-4 font-mono text-xs">
                  {Object.entries(log.request_headers).map(([key, value]) => (
                    <div key={key}>
                      <span className="text-stone-500">{key}:</span>{' '}
                      <span className="text-bark-900 dark:text-[#e8ece9]">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Payload */}
            <div>
              <h3 className="text-sm font-semibold text-bark-900 dark:text-[#e8ece9] mb-2">
                Payload
                {log.payload_size !== null && log.payload_size !== undefined && (
                  <span className="font-normal text-stone-400 ml-2">
                    ({(log.payload_size / 1024).toFixed(1)} KB)
                  </span>
                )}
              </h3>
              <pre className="bg-stone-50 dark:bg-[#1e2520] rounded-lg p-4 text-xs font-mono text-bark-900 dark:text-[#e8ece9] overflow-auto max-h-96">
                {JSON.stringify(log.payload, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
